/**
 * TELV1-487/488 — Emitted embed-snippet artifacts for the EmbedCodeGenerator.
 *
 * Pure strings and builders only: this module must stay import-free (no React,
 * no `@/` app imports) so unit tests exercise it without dragging in the
 * api/react-query layer (`import.meta.env`).
 *
 * RELAY_CORE is the shared INPUT/OUTPUT storage-bridge listener body. It is
 * composed into every variant (generic host page, Qualtrics question JS,
 * Nfield ODIN block) so the relay logic can never drift between platforms.
 * Each variant appends its OWN `tellet:processing` continuation handling —
 * that part is deliberately NOT in RELAY_CORE.
 *
 * INVARIANT: HOST_PAGE_SCRIPT must remain byte-identical to the artifact the
 * generator emitted before this extraction (researchers have it pasted in
 * live surveys; support compares against docs). Guarded by an exact-equality
 * test in __tests__/embedSnippets.test.ts — if you change RELAY_CORE, that
 * test failing is the point.
 */

export interface EmbedQueryParam {
  key: string;
  value: string;
  /**
   * Percent-encode the value. Only for generator-controlled values (e.g.
   * `language`, whose labels carry spaces). Researcher-supplied values are
   * inserted raw — see buildEmbedQuery.
   */
  encodeValue?: boolean;
}

/**
 * TELV1-487 — assemble the embed URL query manually instead of via
 * URLSearchParams. Platform pipe tokens (`${e://Field/ResponseID}`,
 * `{IOM.Info.Serial}`, `[pipe: x]`, `^respid^`) must appear LITERALLY in the
 * pasted HTML for the host platform's piper to substitute them; form-encoding
 * them turns every respondent's ID into the literal token string. Researcher
 * values are inserted exactly as typed — the customer doc's URL-Encoding
 * section instructs pre-encoding literal values, and encoding again here
 * double-encoded them (`iPhone%2015` → `iPhone%252015`).
 *
 * Semantics preserved from the previous URLSearchParams implementation:
 * last-wins de-duplication per key (so a researcher-supplied `language`
 * still overrides the generated one) and skipping of empty keys/values.
 * Keys are encoded (they are plain identifiers, so this is a no-op guard);
 * only values ride raw.
 */
export const buildEmbedQuery = (params: EmbedQueryParam[]): string => {
  const merged = new Map<string, string>();
  params.forEach(({ key, value, encodeValue }) => {
    const k = key.trim();
    const v = value.trim();
    if (!k || !v) return;
    merged.set(encodeURIComponent(k), encodeValue ? encodeURIComponent(v) : v);
  });
  return Array.from(merged.entries())
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
};

// The frame-matching + INPUT/OUTPUT relay body, indented for a listener
// callback at two levels (4 spaces) — the depth it sits at in every variant.
export const RELAY_CORE = `    var frames = document.getElementsByTagName('iframe');
    var frame = null;
    for (var i = 0; i < frames.length; i++) {
      if (event.source === frames[i].contentWindow) { frame = frames[i]; break; }
    }
    if (!frame) return; // ignore messages from non-Tellet frames
    var msg = event.data || {};

    // Persist the state Tellet asks us to store — enables resume after reload.
    // A null/undefined value is a REMOVE (e.g. reset / conversation-not-found
    // cleanup); setAttribute(key, null) would store the literal string "null"
    // and later resume conversation id "null" (404), so remove the attribute.
    if (msg.type === 'INPUT' && msg.data) {
      Object.keys(msg.data).forEach(function (key) {
        var value = msg.data[key];
        if (value === null || value === undefined) {
          frame.removeAttribute(key);
        } else {
          frame.setAttribute(key, value);
        }
      });
    }

    // Return the stored state when Tellet requests it.
    if (msg.type === 'OUTPUT') {
      var attrs = {};
      Array.prototype.forEach.call(frame.attributes, function (a) {
        attrs[a.name] = a.value;
      });
      frame.contentWindow.postMessage(attrs, '*');
    }`;

// H5 — the host page needs this listener ONCE PER PAGE for the embed to behave
// correctly: it relays Tellet's iframe session state (INPUT = store, OUTPUT =
// replay) so the interview resumes across a page reload, and gives a place to
// continue the survey when the interview finishes. Without it a researcher who
// copies only the iframe gets broken reload-resume — the documented Component 2
// (docs/publishing-sharing/survey-embedding.md) that most people skip.
//
// This is iframe-id-agnostic (matches by contentWindow) so it works with the
// generated id="tellet-iframe", and it uses the recommended tellet:processing
// event for continuation instead of the docs example's telletFrame/q<n> click,
// which assumes a different id convention and would throw for id="tellet-iframe".
export const HOST_PAGE_SCRIPT = `<script>
  // Add once per survey page (not per iframe).
  window.addEventListener('message', function (event) {
${RELAY_CORE}

    // Interview finished — continue your survey here.
    if (msg.type === 'tellet:processing') {
      // e.g. document.getElementById('next-button').click();
    }
  });
</script>`;

// TELV1-487 — Qualtrics strips <script> tags from question HTML, so the relay
// cannot ride the iframe snippet: it lives in Question behavior → JavaScript
// instead, wrapped in the first-party addOnReady hook. Differences from the
// generic host script, all deliberate:
//  - Next-button gating via the official disableNextButton()/enableNextButton()
//    question API (Qualtrics' documented mechanism for holding a page).
//  - Completion is recorded in embedded data (per question id), so a respondent
//    who navigates BACK into an already-finished Tellet question is not
//    re-trapped behind a disabled Next button (addOnReady re-fires on every
//    render; without the flag, disableNextButton would run again with no
//    tellet:processing left to release it).
//  - The message listener attaches once per page (addOnReady re-fires), while
//    window.telletQuestion is refreshed on every render so the listener always
//    releases the CURRENT question context.
//  - Fail-open guard: if the iframe never posts anything (blocked by CSP,
//    network failure), Next is re-enabled after 20s rather than trapping the
//    respondent in the survey.
// Indent every non-empty line of a snippet by n spaces (for nesting a shared
// core one level deeper without leaving trailing whitespace on blank lines).
const indentLines = (snippet: string, n: number): string =>
  snippet.replace(/^(?=.)/gm, ' '.repeat(n));

// TELV1-488 — Nfield (NIPO) CAWI: ODIN's << >> script brackets execute inline
// HTML and JavaScript inside a question, so the iframe AND the relay ship as
// ONE paste unit wrapped in a single bracket pair. This is the in-page path
// (tested in-house 2026-07-14) — explicitly NOT App-in-the-Middle, whose
// full-page redirect defeats embedding. The relay is attach-once guarded
// (window.telletRelayAttached) because it travels per-question here rather
// than once per page.
export const buildNfieldBlock = (iframeCode: string): string => `<<
${iframeCode}

<script>
  // Runs in-page inside your ODIN question. One Tellet block per survey page.
  if (!window.telletRelayAttached) {
    window.telletRelayAttached = true;
    window.addEventListener('message', function (event) {
${indentLines(RELAY_CORE, 2)}

      // Interview finished — continue your survey here (e.g. reveal or
      // re-enable your page's Next control).
      if (msg.type === 'tellet:processing') {
        // e.g. document.getElementById('next-button').click();
      }
    });
  }
</script>
>>`;

export const QUALTRICS_QUESTION_JS = `Qualtrics.SurveyEngine.addOnReady(function () {
  // Replace the ENTIRE contents of the JavaScript editor with this snippet —
  // do not paste it inside the editor's default addOnReady stub.
  var qthis = this;
  window.telletQuestion = qthis; // refreshed every render (back-navigation safe)

  // Hold Next until the interview finishes — but never re-trap a respondent
  // who already completed it and navigated back to this question.
  var doneKey = 'telletComplete_' + qthis.questionId;
  var alreadyDone = ('' + Qualtrics.SurveyEngine.getEmbeddedData(doneKey)) === '1';
  if (!alreadyDone) {
    qthis.disableNextButton();
    // Fail-open: if the interview never loads (blocked/network), release Next
    // after 20s instead of leaving the respondent stuck.
    setTimeout(function () {
      if (!window.telletAlive) { window.telletQuestion.enableNextButton(); }
    }, 20000);
  }

  if (window.telletRelayAttached) { return; } // attach the relay once per page
  window.telletRelayAttached = true;
  window.addEventListener('message', function (event) {
${RELAY_CORE}
    window.telletAlive = true;

    // Interview finished — record completion and release the Next button.
    if (msg.type === 'tellet:processing') {
      Qualtrics.SurveyEngine.setEmbeddedData(doneKey, '1');
      window.telletQuestion.enableNextButton();
      // Auto-advance instead: window.telletQuestion.clickNextButton();
    }
  });
});`;
